import { UserProfile, PetDetails } from '../types';

const STORAGE_KEY_PREFIX = 'feisty_pets_user_';

class StorageService {
  private getCurrentUserId(): string | null {
    // Pega o ID do usuário logado do localStorage
    const authData = localStorage.getItem('feisty_pets_auth');
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        return parsed.uid || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  private getStorageKey(): string {
    const userId = this.getCurrentUserId();
    if (!userId) {
      // Se não houver usuário logado, usa uma chave genérica (não deveria acontecer)
      return `${STORAGE_KEY_PREFIX}guest`;
    }
    return `${STORAGE_KEY_PREFIX}${userId}`;
  }

  getUserProfile(): UserProfile | null {
    try {
      const key = this.getStorageKey();
      const data = localStorage.getItem(key);
      if (!data) return null;
      return JSON.parse(data);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      return null;
    }
  }

  saveUserProfile(profile: UserProfile): void {
    try {
      const key = this.getStorageKey();
      localStorage.setItem(key, JSON.stringify(profile));
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
    }
  }

  getPetDetails(petId: string): PetDetails | null {
    try {
      const profile = this.getUserProfile();
      if (!profile || !profile.petDetails) return null;
      return profile.petDetails[petId] || null;
    } catch (error) {
      console.error('Erro ao carregar detalhes do pet:', error);
      return null;
    }
  }

  updatePetDetails(petId: string, details: PetDetails): void {
    try {
      const profile = this.getUserProfile();
      if (!profile) return;

      if (!profile.petDetails) {
        profile.petDetails = {};
      }

      profile.petDetails[petId] = details;
      this.saveUserProfile(profile);
    } catch (error) {
      console.error('Erro ao atualizar detalhes do pet:', error);
    }
  }

  createDefaultProfile(): UserProfile {
    const userId = this.getCurrentUserId();
    const username = userId ? `Colecionador_${userId.substring(0, 6)}` : 'Colecionador';
    
    return {
      id: userId || 'guest',
      username,
      avatar: 'https://public.readdy.ai/ai/img_res/69e1c4657f955aa4c8382b05960ad97b.jpg',
      collection: {
        captured: [],
        wishlist: []
      },
      achievements: [
        {
          id: 'first-capture',
          name: 'Primeira Captura',
          description: 'Capture seu primeiro Feisty Pet',
          icon: 'fa-solid fa-star',
          unlocked: false
        },
        {
          id: 'collector-beginner',
          name: 'Colecionador Iniciante',
          description: 'Capture 5 Feisty Pets',
          icon: 'fa-solid fa-trophy',
          unlocked: false
        },
        {
          id: 'collector-dedicated',
          name: 'Colecionador Dedicado',
          description: 'Capture 10 Feisty Pets',
          icon: 'fa-solid fa-medal',
          unlocked: false
        },
        {
          id: 'collector-expert',
          name: 'Colecionador Expert',
          description: 'Capture 25 Feisty Pets',
          icon: 'fa-solid fa-gem',
          unlocked: false
        },
        {
          id: 'master-collector',
          name: 'Mestre Colecionador',
          description: 'Complete 100% da coleção',
          icon: 'fa-solid fa-crown',
          unlocked: false
        }
      ],
      createdAt: new Date().toISOString()
    };
  }

  exportCollection(): string {
    const profile = this.getUserProfile();
    return JSON.stringify(profile, null, 2);
  }

  clearUserData(): void {
    const key = this.getStorageKey();
    localStorage.removeItem(key);
  }

  // Método para migrar dados antigos (se existirem) para o novo formato
  migrateOldData(): void {
    const oldKey = 'feisty_pets_profile';
    const oldData = localStorage.getItem(oldKey);
    
    if (oldData && this.getCurrentUserId()) {
      try {
        const profile = JSON.parse(oldData);
        const newKey = this.getStorageKey();
        
        // Se não existir dados no novo formato, migra os antigos
        if (!localStorage.getItem(newKey)) {
          localStorage.setItem(newKey, oldData);
          console.log('Dados migrados para o novo formato');
        }
        
        // Remove os dados antigos
        localStorage.removeItem(oldKey);
      } catch (error) {
        console.error('Erro ao migrar dados:', error);
      }
    }
  }
}

export const storageService = new StorageService();
